<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import { tempoCurto, segundosDesde } from './helpers/tempo';

const props = defineProps({
  card: { type: Object, required: true },
  showValue: { type: Boolean, default: false },
  currency: { type: String, default: 'BRL' },
  // CRM-01h fase 3: seleção para o "mover em bloco".
  selected: { type: Boolean, default: false },
  // Com qualquer seleção na tela, a caixa fica à vista em todos os cards.
  selectionActive: { type: Boolean, default: false },
  // A etapa inteira está selecionada: o card vai junto e a caixa dele não desmarca sozinha.
  selectionLocked: { type: Boolean, default: false },
});

const emit = defineEmits(['open', 'toggleSelect']);

const { t } = useI18n();

// CRM-04 R2-T7 (K1, §6.2): a face é a do negócio. O nome é o título ou o contato principal, e quem
// decide é o servidor (`display_name`). O telefone, a última mensagem, a caixa, as etiquetas e as
// não lidas ficaram no painel; os campos antigos (`conversation`, `contact`, `inbox`, `assignee`), que o
// servidor ainda manda por compatibilidade, a face não lê.
const cardName = computed(
  () => props.card.display_name || props.card.title || t('KANBAN.CARD.NO_NAME')
);

// Até 3 avatares, o principal primeiro (é a ordem do servidor), e "+N" com o resto.
const AVATARES = 3;
const contatos = computed(() => (props.card.contacts || []).slice(0, AVATARES));
const outrosContatos = computed(() =>
  Math.max(0, (props.card.contacts_count || 0) - contatos.value.length)
);

// K5: quem o negócio espera, com o tempo parado desde a última mensagem. "nenhum" (tudo resolvido)
// e o nulo não ocupam a face.
const AGUARDANDO = {
  ia: 'bg-n-violet-3 text-n-violet-11',
  cliente: 'bg-n-blue-3 text-n-blue-11',
  equipe: 'bg-n-amber-3 text-n-amber-11',
};

const rotuloDeQuem = quem =>
  ({
    ia: t('KANBAN.CARD.AGUARDANDO.ia'),
    cliente: t('KANBAN.CARD.AGUARDANDO.cliente'),
    equipe: t('KANBAN.CARD.AGUARDANDO.equipe'),
  })[quem];

const aguardando = computed(() => {
  const quem = props.card.aguardando;
  if (!AGUARDANDO[quem]) return null;
  const rotulo = rotuloDeQuem(quem);
  const desde = props.card.aguardando_desde;
  return {
    classe: AGUARDANDO[quem],
    texto: desde
      ? t('KANBAN.CARD.AGUARDANDO_HA', {
          quem: rotulo,
          tempo: tempoCurto(segundosDesde(desde), t),
        })
      : rotulo,
  };
});

const owner = computed(() => props.card.owner);

// Aging: the longer a card sits untouched in a stage, the louder it gets.
const agingClass = computed(() => {
  const days = props.card.days_in_stage;
  if (days >= 7) return 'text-n-ruby-11 bg-n-ruby-3';
  if (days >= 3) return 'text-n-amber-11 bg-n-amber-3';
  return 'text-n-slate-11 bg-n-alpha-2';
});

const agingLabel = computed(() => {
  const days = props.card.days_in_stage;
  if (days <= 0) return t('KANBAN.CARD.TODAY');
  return t('KANBAN.CARD.DAYS_IN_STAGE', { count: days });
});

const formattedValue = computed(() => {
  if (!props.card.value_cents) return null;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: props.currency || 'BRL',
  }).format(props.card.value_cents / 100);
});

const mostrarCaixa = computed(() => props.selected || props.selectionActive);

// CRM-04 C8: o card inteiro abre o painel (a ideia dos PRs #132 e #178). O card também se
// arrasta, e um arraste pode acabar em clique: se a mão andou mais que isto entre apertar e
// soltar, foi arraste. O que tem ação própria dentro do card (a caixa de seleção) não deixa o
// clique subir até aqui.
const LIMIAR_DO_ARRASTE = 6;
let inicio = null;

const onPointerDown = event => {
  inicio = { x: event.clientX, y: event.clientY };
};

const onClick = event => {
  const de = inicio;
  inicio = null;
  const andou = de ? Math.hypot(event.clientX - de.x, event.clientY - de.y) : 0;
  if (andou > LIMIAR_DO_ARRASTE) return;
  emit('open', props.card.id);
};
</script>

<template>
  <div
    class="relative flex flex-col gap-2 p-3 border rounded-lg cursor-pointer group bg-n-solid-1 hover:border-n-slate-6 active:cursor-grabbing"
    :class="selected ? 'border-n-brand' : 'border-n-weak'"
    @pointerdown="onPointerDown"
    @click="onClick"
  >
    <!-- CRM-01h fase 3: a caixa de seleção aparece ao passar o mouse e fica à vista enquanto houver
         seleção. Na face do negócio (CRM-04) ela mora no canto, porque o avatar virou a fileira de
         contatos. O pointerdown não sobe: clicar na caixa não pode começar a arrastar o card. O
         clique também não: aqui se seleciona, não se abre o painel. -->
    <span
      data-selecao
      class="absolute items-center justify-center hidden top-2.5 ltr:right-2.5 rtl:left-2.5 size-5 group-hover:flex"
      :class="{ '!flex': mostrarCaixa }"
      :title="$t('KANBAN.CARD.SELECT')"
      @pointerdown.stop
      @mousedown.stop
      @click.stop
    >
      <Checkbox
        :model-value="selected"
        :disabled="selectionLocked"
        :aria-label="$t('KANBAN.CARD.SELECT')"
        @change="emit('toggleSelect', card)"
      />
    </span>

    <p
      data-face="nome"
      class="text-sm font-medium truncate text-n-slate-12 ltr:pr-6 rtl:pl-6"
      :title="cardName"
    >
      {{ cardName }}
    </p>

    <div
      v-if="contatos.length"
      data-face="contatos"
      class="flex items-center gap-1.5"
    >
      <div class="flex items-center -space-x-1.5 rtl:space-x-reverse">
        <span
          v-for="contato in contatos"
          :key="contato.id"
          class="flex rounded-full ring-2 ring-n-solid-1"
          :title="contato.name"
        >
          <Avatar
            :name="contato.name || $t('KANBAN.CARD.NO_NAME')"
            :src="contato.avatar_url || ''"
            :size="20"
            rounded-full
          />
        </span>
      </div>
      <span v-if="outrosContatos" class="text-xs text-n-slate-11 tabular-nums">
        {{ $t('KANBAN.CARD.MORE_CONTACTS', { count: outrosContatos }) }}
      </span>
    </div>

    <span
      v-if="aguardando"
      data-face="aguardando"
      class="self-start px-1.5 py-0.5 text-xs rounded whitespace-nowrap"
      :class="aguardando.classe"
    >
      {{ aguardando.texto }}
    </span>

    <p
      v-if="card.proximo_passo"
      data-face="proximo-passo"
      class="flex items-start gap-1 text-xs text-n-slate-12"
      :title="$t('KANBAN.CARD.NEXT_STEP')"
    >
      <Icon
        icon="i-lucide-corner-down-right"
        class="flex-shrink-0 mt-0.5 size-3 text-n-slate-10"
      />
      <span class="line-clamp-2">{{ card.proximo_passo }}</span>
    </p>

    <div class="flex items-center gap-2 pt-1">
      <span
        v-if="showValue && formattedValue"
        data-face="valor"
        class="text-xs font-medium text-n-teal-11 tabular-nums"
      >
        {{ formattedValue }}
      </span>
      <div class="flex-1" />
      <span
        v-if="owner"
        data-face="responsavel"
        class="flex flex-shrink-0"
        :title="$t('KANBAN.CARD.OWNER', { name: owner.name })"
      >
        <Avatar
          :name="owner.name || $t('KANBAN.CARD.NO_NAME')"
          :src="owner.avatar_url || ''"
          :size="20"
          rounded-full
        />
      </span>
      <span
        data-face="tempo-na-etapa"
        class="flex-shrink-0 px-1.5 py-0.5 text-xs rounded whitespace-nowrap"
        :class="agingClass"
        :title="agingLabel"
      >
        {{ agingLabel }}
      </span>
    </div>
  </div>
</template>
